/**
 * A pricing strategy — the logic, named, and applied to a set of homes.
 *
 * Called a Dynamic Pricing Strategy, or Revenue Management. It is one object, it has a name, it has a
 * list of homes it applies to. Change the strategy and every home on it changes together.
 *
 * THE ARCHITECTURE IS THE PRODUCT OWNER'S, AND SO IS THE ARGUMENT
 *
 *   "这个定价逻辑…可以把定价逻辑存起来,然后另外一个房子它可以直接 apply…你把它写死在
 *    Property Setup 里面的坏处是每一个 Property 你都要重新 setup。"
 *
 * There are 126 active homes and perhaps four ways this business prices. Per-property rules meant
 * typing one policy 126 times and being unable to change it once: a rethink of the Seattle ladder
 * would have been 95 separate edits, so it would never have happened.
 *
 * THE SPLIT
 *
 *   THE PROPERTY holds what is true of THAT HOME — its base monthly rent, its annual rate, the
 *   minimum it may not go below, its deposit ladder, its cleaning prices, its owner's pet rules.
 *
 *   THE MODEL holds the POLICY — what each calendar month is worth, what a longer stay is worth,
 *   and what booking early or late is worth. One strategy, many homes.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * ONE SIGN CONVENTION, EVERYWHERE
 *
 * Every percentage in this file is a SIGNED ADJUSTMENT to the rent. +20 makes it dearer, −10 makes
 * it cheaper, 0 changes nothing. MINUS IS CHEAPER, in all three sections, with no exceptions.
 *
 * An earlier version had `percent: 8` meaning "8% off" in one place and "8% on" in another, with
 * nothing between them but a variable name.
 * ────────────────────────────────────────────────────────────────────────────────────────────
 *
 * WHAT IS NOT HERE, ALL OF IT CUT BY THE PRODUCT OWNER RATHER THAN UNFINISHED
 *
 *   annual increase · per-month Min/Max guardrails · gap alignment · channel markup and fees
 *
 * And one more, cut after it was built: NAMED SEASONS. See below.
 */

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * TWELVE MONTHS, TWELVE PERCENTAGES. THAT IS THE WHOLE OF SEASONALITY.
 *
 *   monthAdjustments: [-10, -10, 0, 0, 8, 20, 20, 20, 5, 5, -10, -10]
 *                       J    F   M  A  M   J   J   A  S  O   N    D
 *
 * THIS REPLACES A NAMED-SEASON OBJECT THAT WAS BUILT, SHIPPED, AND THEN CUT
 *
 *   "我们从头到尾都不用去设季节，就只要设月份就好了，季节的加成数就已经在月份里面体现了，不要
 *    再去额外搞季节这个东西…只是自己找麻烦，把自己搞得太复杂。"
 *
 * He is right, and the way he is right is worth writing down. A named season — "Summer Peak, Jun
 * to Aug, +20%" — carries no information that twelve numbers do not. What it adds is a second
 * structure that can disagree with the first: seasons that overlap, seasons that leave a gap,
 * seasons that need a validator and an error state and a colour legend, all to express a list of
 * twelve numbers. Every one of those failure modes was invented by the abstraction, not by the
 * business. The month was always the unit; the season was decoration with a maintenance cost.
 *
 * The rent for a month is `base × (1 + adjustment / 100)`. On a $5,000 base, August at +50% is
 * $7,500 and September at +10% is $5,500. Nothing is averaged, nothing is split, and a stay across
 * both is charged $7,500 and then $5,500.
 */
export const NO_ADJUSTMENTS = () => Array(12).fill(0)

/** The signed percentage for a calendar month. 0 = January. Missing or nonsense means no change. */
export function monthPercent(strategy, month0) {
  const v = strategy?.monthAdjustments?.[month0]
  return Number.isFinite(v) ? v : 0
}

export const createStrategy = ({
  id,
  name,
  /** Twelve signed percentages, January first. */
  monthAdjustments = NO_ADJUSTMENTS(),
  /**
   * Length of stay, IN WHOLE MONTHS ONLY.
   *
   *   [{ months: 2, percent: -2 }, { months: 3, percent: -4 }, { months: 6, percent: -6 }]
   *
   * The design sheet had night bands — 30–44, 45–59, 60–89. The Product Owner cut them to whole
   * months: "不能按什么 35 天、45 天这么弹性，就是一个月、两个月、三个月". A stay of 3 months and
   * 20 days takes the 3-month row.
   *
   * EACH NUMBER IS THE WHOLE ADJUSTMENT AT THAT LENGTH, NOT A STEP ADDED TO THE ROW ABOVE. Three
   * months on the ladder above is −4%, not −6%. See `ladderPreview` — the screen prints the
   * resulting figure beside every row, because this is precisely the question the numbers alone
   * cannot answer.
   */
  monthTiers = [],
  /**
   * Lead time, as BANDS of days before move-in — not two special rules.
   *
   *   [{ id, label: 'Last minute', fromDays: 0, toDays: 14, percent: -10 },
   *    { id, label: 'Soon',        fromDays: 15, toDays: 30, percent: -5 },
   *    { id, label: 'Far out',     fromDays: 120, toDays: 365, percent: 5 }]
   *
   * The first version had exactly two named rules with their own boxes and their own paragraphs.
   * The Product Owner cut that too:
   *
   *   "不要把它分开…就是当天的日期距离今天还有几天的时候设这个范围，然后可以正负就好了…因为你
   *    last minute discount，你可能是会想设不同 tier 对吧？越接近 discount 越大，目前是不能设
   *    tier 的。"
   *
   * Which is the same shape as the length ladder and the month row: a range, a signed percentage.
   * Three sections, one idea, and the deepest discount can now sit closest to the date.
   *
   * Both ends are real numbers. An unbounded band was offered once and cut: "原本你放一个无限大，
   * 这个是不现实的，没有人可以输入的了无限大这个数字."
   */
  leadTime = [],
  /**
   * The homes this strategy applies to. The strategy owns the list, not the other way round.
   *
   *   "你还要把那个 apply 弄上去，比如说我要套用到哪几个 Listing 上面去。"
   */
  appliesTo = [],
  note = '',
}) => ({ id, name, monthAdjustments, monthTiers, leadTime, appliesTo, note })

// ── Length of stay ──────────────────────────────────────────────────────────────────────────

/**
 * The tier for a stay of `months` WHOLE months. The highest tier reached wins; tiers never stack.
 *
 * A six-month stay that passes a 2-, 3- and 6-month row takes the 6-month row only. Adding them
 * would compound three adjustments the person who typed them never chose to compound, and it would
 * do it on the longest and most valuable leases.
 */
export function monthTierFor(strategy, months) {
  const tiers = strategy?.monthTiers
  if (!Array.isArray(tiers) || tiers.length === 0) return null
  return tiers
    .filter((t) => Number.isFinite(t?.months) && Number.isFinite(t?.percent) && months >= t.months)
    .sort((a, b) => b.months - a.months)[0] ?? null
}

/**
 * What each rung of the ladder actually does — the column the Product Owner asked for.
 *
 *   "我看不出…有没有叠加？我建议你旁边再显示一个空白框，这样就会很清楚了。"
 *
 * He could not tell from the numbers whether the rows add up, and nothing on the screen said. The
 * answer is printed instead of explained: the base rent is 100%, and each rung shows what it
 * becomes. Three months at −4% reads 96%, not 94%, and nobody has to take the rule on trust.
 *
 * IT IS A PERCENTAGE AND NOT A DOLLAR FIGURE, which is the second thing he was right about. An
 * earlier version asked for a sample rent to be typed in at the top of the page so the rows could
 * show money. That box belonged to nobody — no home has it, the quote never reads it — and a
 * control that exists only to make another control legible is one control too many.
 */
export const asPercentOfBase = (percent) => 100 + percent

export function ladderPreview(strategy) {
  return [...(strategy?.monthTiers ?? [])]
    .sort((a, b) => a.months - b.months)
    .map((t) => ({
      ...t,
      /** Not a running total. This is the whole adjustment at this length. */
      effectivePercent: t.percent,
      percentOfBase: asPercentOfBase(t.percent),
    }))
}

// ── Lead time ───────────────────────────────────────────────────────────────────────────────

export const daysBetweenDates = (fromIso, toIso) =>
  Math.round((Date.parse(toIso.slice(0, 10) + 'T00:00:00Z') - Date.parse(fromIso.slice(0, 10) + 'T00:00:00Z')) / 86_400_000)

/**
 * Does a band contain this many days out?
 *
 * `toDays: null` is tolerated as "and beyond" for any band written that way, but the screen no
 * longer offers it: "原本你放一个无限大，这个是不现实的，没有人可以输入的了无限大这个数字." Every
 * band the UI creates has two real numbers, and days outside every band get no adjustment — which
 * is how Guesty's own rate strategy behaves and how anybody reading a table expects it to.
 */
export const bandContains = (band, days) =>
  Number.isFinite(band?.fromDays) &&
  days >= band.fromDays &&
  (band.toDays == null || days <= band.toDays)

/**
 * How far ahead this booking is, turned into at most ONE adjustment.
 *
 * `asOf` is a parameter, like every other clock in this product. A price that depends on when the
 * code happened to run is a price nobody can reproduce, and a renter reloading a quote must not see
 * a different number from the one they were sent.
 *
 * Days outside every band means no adjustment, which is the ordinary case and stays ordinary.
 */
export function leadTimeAdjustmentFor(strategy, { checkIn, asOf }) {
  const bands = strategy?.leadTime
  if (!Array.isArray(bands) || bands.length === 0 || !asOf || !checkIn) return null
  const daysOut = daysBetweenDates(asOf, checkIn)
  if (daysOut < 0) return null // the stay has already started; lead time is not a thing any more
  const band = bands.find((b) => bandContains(b, daysOut) && Number.isFinite(b.percent))
  return band ? { kind: 'LEAD_TIME', label: band.label, percent: band.percent, daysOut } : null
}

/**
 * Two bands covering the same number of days is a bug that pays out: whichever comes first in the
 * array wins, so the same strategy prices differently after somebody reorders the rows. Reported so
 * the screen can show it, never resolved by a tie-break nobody would look up.
 */
export function leadTimeOverlaps(strategy) {
  const bands = strategy?.leadTime ?? []
  const clashes = []
  for (let i = 0; i < bands.length; i++) {
    for (let j = i + 1; j < bands.length; j++) {
      // 0…1095 days is three years out — past anything this business quotes, and cheap to scan.
      for (let d = 0; d <= 1095; d++) {
        if (bandContains(bands[i], d) && bandContains(bands[j], d)) {
          clashes.push({ a: bands[i], b: bands[j], onDay: d })
          break
        }
      }
    }
  }
  return clashes
}

// ── Putting them together ───────────────────────────────────────────────────────────────────

/**
 * The adjustments that apply to the WHOLE stay, in a fixed order, each shown as its own line.
 *
 * The month adjustment is not here: it is applied per calendar month inside price.js, because a
 * stay across October, November and December is three different rents.
 *
 * ORDER MATTERS AND IS NOT COMMUTATIVE IN THE WAY PEOPLE ASSUME. A 10% reduction followed by an
 * 8% reduction is 17.2% off, not 18%. Either arithmetic is defensible; only one can be the one the
 * business means, so it is fixed here, applied in this order everywhere, and every step reported.
 * A total that cannot be reconstructed line by line is not a price.
 */
export function applyAdjustments(rentCents, { monthTier, leadTime }) {
  const steps = []
  let cents = rentCents
  if (monthTier && monthTier.percent !== 0) {
    const before = cents
    cents = Math.round(cents * (1 + monthTier.percent / 100))
    steps.push({ kind: 'LENGTH', percent: monthTier.percent, months: monthTier.months, beforeCents: before, afterCents: cents })
  }
  if (leadTime && leadTime.percent !== 0) {
    const before = cents
    cents = Math.round(cents * (1 + leadTime.percent / 100))
    steps.push({ ...leadTime, beforeCents: before, afterCents: cents })
  }
  return { cents, steps }
}

/** Plain English for one adjustment step, used in refusals and on the quote. */
export function describeStep(step) {
  const pct = `${step.percent > 0 ? '+' : ''}${step.percent}%`
  if (step.kind === 'LENGTH') return `${pct} for a stay of ${step.months} month${step.months === 1 ? '' : 's'} or more`
  if (step.kind === 'LEAD_TIME') {
    return `${pct}${step.label ? ` · ${step.label}` : ''} — booked ${step.daysOut} day${step.daysOut === 1 ? '' : 's'} before move-in`
  }
  return pct
}

/**
 * THE STRATEGY'S WORKING ON BUILD QUOTE, LINE BY LINE, ADDING UP TO THE RENT LINE BELOW IT.
 *
 * "Before the strategy", then each whole-stay step in the order it was applied, then — when the rent was
 * written in whole dollars (2026-10-21) — "Rounded to the dollar" with what that moved. Without that last line
 * the working stopped at $12,575.40 above a Rent of $12,575, and the one card whose job is legible arithmetic
 * left $0.40 unexplained (design review 2026-10-21, finding 2).
 *
 * Only when nobody typed a month: a typed month is shown beside its computed figure, and the difference it makes
 * is a person's decision, not rounding.
 *
 * Returns [{ label, cents, kind: 'START' | 'STEP' | 'ROUNDING', percent? }].
 */
export function strategyWorking(option, strategyName = option?.strategy?.name ?? null) {
  if (!option?.adjustments?.length) return []
  const lines = [{
    kind: 'START',
    label: `Before the strategy${strategyName ? ` · ${strategyName}` : ''}`,
    cents: option.listRentCents,
  }]
  for (const step of option.adjustments) {
    lines.push({ kind: 'STEP', label: describeStep(step), cents: step.afterCents - step.beforeCents, percent: step.percent })
  }
  const lastCents = option.adjustments.at(-1).afterCents
  if (!option.rentWasOverridden && Number.isFinite(option.quotedRentCents) && option.quotedRentCents !== lastCents) {
    lines.push({ kind: 'ROUNDING', label: 'Rounded to the dollar', cents: option.quotedRentCents - lastCents })
  }
  return lines
}

/**
 * How many homes this strategy applies to.
 *
 * The list lives on the strategy now, so this is a length rather than a search. The old signature —
 * counting a property→strategy map — is kept working because Property setup still writes that way,
 * and one direction has to be the truth: the strategy's own list is.
 */
export const usageOf = (strategyId, assignments) => {
  if (Array.isArray(assignments)) {
    return assignments.find((m) => m.id === strategyId)?.appliesTo?.length ?? 0
  }
  return Object.values(assignments ?? {}).filter((id) => id === strategyId).length
}

/** The property→strategy map the quote reads, built from every strategy's own list. */
export const assignmentsFrom = (strategies) =>
  Object.fromEntries((strategies ?? []).flatMap((m) => (m.appliesTo ?? []).map((unitId) => [unitId, m.id])))
